import {useSelector} from '@xstate/react';
import React, {createContext, useContext, PropsWithChildren} from 'react';
import {ActorRefFrom} from 'xstate';

import {ConcentrationLayerMachine} from './machine/ConcentrationLayerMachine';

/**
 * Context for the concentration layer machine.
 * Provides access to the machine actor ref throughout the component tree.
 */
const ConcentrationLayerMachineCtx = createContext<ActorRefFrom<typeof ConcentrationLayerMachine> | null>(
  null
);

type ConcentrationLayerMachineActor = ActorRefFrom<typeof ConcentrationLayerMachine>;

export function useConcentrationLayerSelector<T = unknown>(
  selector: (snapshot: ReturnType<ConcentrationLayerMachineActor['getSnapshot']>) => T,
  compare?: (a: T, b: T) => boolean
): T {
  const actorRef = useContext(ConcentrationLayerMachineCtx)!;
  return useSelector(actorRef, selector, compare);
}

export const useConcentrationLayerActorRef = (): ConcentrationLayerMachineActor =>
  useContext(ConcentrationLayerMachineCtx)!;

// Provider component that accepts existing machine actor
export interface ConcentrationLayerMachineProviderProps {
  actor: ActorRefFrom<typeof ConcentrationLayerMachine> | null | undefined;
}

export function ConcentrationLayerMachineProvider({
  children,
  actor,
}: PropsWithChildren<ConcentrationLayerMachineProviderProps>): JSX.Element {
  return (
    <ConcentrationLayerMachineCtx.Provider value={actor ?? null}>
      {children}
    </ConcentrationLayerMachineCtx.Provider>
  );
}
